import { useState, useEffect, useCallback, useContext } from "react";

import { SocketContext } from "../../../context/Socket/SocketContext";
import { checkServiceWindow } from "../../../services/wabaTemplates.api";

/**
 * Ventana de servicio de 24h de WhatsApp Cloud API (Meta). Doc 0367 (H8).
 *
 * Consulta el estado por ticket vía GET /waba-templates/check-window/:ticketId
 * (el backend ya devuelve needsTemplate:false para canales que no son WABA, por lo
 * que este hook es agnóstico) y se reabre en tiempo real cuando entra un mensaje del
 * cliente (appMessage fromMe=false) por Socket.IO.
 */
export default function useServiceWindow({ ticketId }) {
  const socketManager = useContext(SocketContext);
  const [needsTemplate, setNeedsTemplate] = useState(false);

  const check = useCallback(async () => {
    if (!ticketId) {
      setNeedsTemplate(false);
      return;
    }
    try {
      const { data } = await checkServiceWindow(ticketId);
      setNeedsTemplate(!!data?.needsTemplate);
    } catch {
      // Silencioso: un fallo de red no debe bloquear el input (fail-open).
      setNeedsTemplate(false);
    }
  }, [ticketId]);

  useEffect(() => {
    check();
  }, [check]);

  // Reabrir la ventana cuando el cliente escribe (inbound) — mismo canal que useTicketSocket.
  useEffect(() => {
    if (!ticketId) return undefined;
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const handleAppMessage = (data) => {
      const msgTicketId = data?.message?.ticketId;
      if (!msgTicketId || String(msgTicketId) !== String(ticketId)) return;
      if (data.action === "create" && data.message && data.message.fromMe === false) {
        check();
      }
    };

    socket.on(`company-${companyId}-appMessage`, handleAppMessage);
    return () => {
      if (socket && typeof socket.off === "function") {
        socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      }
    };
  }, [ticketId, socketManager, check]);

  return { needsTemplate, recheck: check };
}
